/**
 * External dependencies
 */
import KeenSlider from "keen-slider";

/**
 * Internal dependencies
 */
import {
	getBlockClassName,
	hasArrows,
	hasDots,
	shouldShowOverflow,
	wrap,
} from "./utils";

(function () {
	const sliders = [];
	let isInitialized = false;

	const init = () => {
		document
			.querySelectorAll(`.${getBlockClassName()}:not(.js-modal-has-been-init)`)
			.forEach(setupDialogs);
		document
			.querySelectorAll(
				`.${getBlockClassName()}__dialog-slider:not(.js-has-been-init)`
			)
			.forEach(setupSlider);
	};

	window.addEventListener("DOMContentLoaded", () => {
		if (!isInitialized) {
			isInitialized = true;
			init();
		}
	});

	window.addEventListener("onReinitRequest", init);

	const setupDialogs = (el) => {
		const button = el.querySelector(".dialog-button");
		const dialog = el.querySelector(`.${getBlockClassName()}__dialog`);
		el.classList.add("js-modal-has-been-init");

		button.addEventListener("click", () => {
			dialog.open = true;
			dialog.classList.add("is-open");
			document.documentElement.classList.add("no-scroll");
		});

		dialog.addEventListener("click", (event) => {
			if (event.target.closest(`.${getBlockClassName()}__dialog__inner`)) {
				return;
			}

			document.documentElement.classList.remove("no-scroll");
			dialog.classList.remove("is-open");
		});

		window.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				document.documentElement.classList.remove("no-scroll");
				dialog.classList.remove("is-open");
			}
		});
	};

	const setupSlider = (el) => {
		el.classList.add("js-has-been-init");

		// Make slider selector '.ab-block-location-card__dialog-slider'
		const className = `${getBlockClassName()}__dialog-slider`;

		const wrapper = el.querySelector(`.${className}__slider`);
		if (!wrapper) return;

		wrapper.classList.add("keen-slider");

		if (shouldShowOverflow()) {
			wrapper.classList.add("show-overflow");
		}

		const header = el.querySelector(`.${className}__header`);

		const controls = createControls(`${className}__controls`);
		header.appendChild(controls);

		const cards = wrapper.querySelectorAll(":scope > *");
		cards.forEach((card) => wrap(card, "keen-slider__slide"));

		const els = {
			wrapper,
			header,
			slides: el.querySelectorAll(".keen-slider__slide"),
			prev: el.querySelector(`.${className}__controls__prev`),
			next: el.querySelector(`.${className}__controls__next`),
			dots: el.querySelector(`.${className}__controls__dots`),
		};

		/**
		 * If you change the spacing or slides per view here, be sure
		 * to change the unmounted styles in style.scss as well, to prevent CLS.
		 */
		const slider = createSlider(els, {
			renderMode: "performance",
			// loop: true,
			slides: {
				perView: 1,
				spacing: 0,
			},
		});

		if (!slider) {
			return false;
		}

		sliders.push(slider);
	};

	const createControls = (className = "controls") => {
		const controls = document.createElement("div");
		controls.classList.add(className);

		if (hasDots()) {
			const dots = document.createElement("div");
			dots.classList.add(`${className}__dots`);
			controls.appendChild(dots);
		}

		if (hasArrows()) {
			const arrows = document.createElement("div");
			arrows.classList.add(`${className}__arrows`);

			const prev = document.createElement("button");
			prev.classList.add(`${className}__prev`);

			const next = document.createElement("button");
			next.classList.add(`${className}__next`);

			arrows.appendChild(prev);
			arrows.appendChild(next);
			controls.appendChild(arrows);
		}

		return controls;
	};

	const createSlider = (els, options) => {
		const hasSlides = els.slides && els.slides.length > 0;

		if (!hasSlides) {
			return false;
		}

		const addArrowEvents = (instance) => {
			if (!hasArrows()) {
				return false;
			}

			els.prev.addEventListener("click", () => instance.prev());
			els.next.addEventListener("click", () => instance.next());
		};

		const buildDots = (instance) => {
			if (!hasDots()) {
				return false;
			}

			const {
				slides: { perView: slidesPerView },
			} = instance.options;
			const numOfDots = els.slides.length - slidesPerView + 1;

			els.dots.innerHTML = "";

			for (let i = 0; i < numOfDots; i++) {
				const dot = document.createElement("button");
				dot.addEventListener("click", () => instance.moveToIdx(i));
				els.dots.appendChild(dot);
			}
		};

		const setActiveDots = (instance) => {
			if (!hasDots()) {
				return false;
			}

			const { rel: relativeSlide } = instance.track.details;
			const dots = els.dots.querySelectorAll("button");

			dots.forEach((dot, index) => {
				dot.classList.toggle("is-active", relativeSlide === index);
			});
		};

		const setActiveSlides = (instance) => {
			const {
				slides: { perView: slidesPerView },
			} = instance.options;
			const { rel: relativeSlide } = instance.track.details;

			els.slides.forEach((slide, index) => {
				slide.classList.toggle(
					"is-active",
					index >= relativeSlide && index < relativeSlide + slidesPerView
				);
			});
		};

		const calcOpacity = (instance) => {
			instance.track.details.slides.forEach(({ portion }, index) => {
				els.slides[index].style.setProperty(
					"--opacity",
					Math.min(1, Math.max(0.25, portion / 0.45)).toPrecision(3)
				);
			});
		};

		const slider = new KeenSlider(els.wrapper, {
			...options,
			created: (instance) => {
				addArrowEvents(instance);
				buildDots(instance);

				setActiveDots(instance);
				setActiveSlides(instance);
				calcOpacity(instance);

				els.instance = instance;

				els.wrapper.classList.add("is-mounted");

				els.slides.forEach((slideEl, index) => {
					const link = slideEl.querySelector("button");
					if (!link) return;

					link.addEventListener("focus", () => {
						const {
							slides: { perView: slidesPerView },
						} = instance.options;

						const { rel: relativeSlide } = instance.track.details;

						if (index + 1 > relativeSlide + slidesPerView) {
							instance.moveToIdx(index + 1 - slidesPerView);
						} else if (index < relativeSlide) {
							instance.moveToIdx(index);
						}
					});
				});
			},
			detailsChanged: (instance) => {
				setActiveDots(instance);
				setActiveSlides(instance);
				calcOpacity(instance);
			},
			slideChanged: (instance) => {
				setActiveDots(instance);
				setActiveSlides(instance);
				calcOpacity(instance);
			},
			// dragStarted: () => {
			// 	els.wrapper.classList.add('is-dragging');
			// },
			dragged: (instance) => {
				els.wrapper.classList.add("is-dragging");
				calcOpacity(instance);
			},
			dragEnded: () => {
				setTimeout(() => els.wrapper.classList.remove("is-dragging"), 250);
			},
		});

		window.addEventListener("onSliderRefreshRequest", (e) => {
			if (!e.detail.block.contains(els.wrapper)) return;
			setActiveDots(slider);
			setActiveSlides(slider);
			calcOpacity(slider);
			slider.update();
		});

		return slider;
	};
})();
